// Revenue.tsx
// @ts-ignore
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSwipeable } from "react-swipeable";
import { api } from "../../../../api/client";
import { useActiveCompanyId } from "../../../../api/useActiveCompanyId";
import { useActiveCompanyIdApi } from "../../../../api/useActiveCompanyIdApi";
import { isIntentionalMonthSwipe } from "../../monthSwipe";
import "../../monthNavigation.scss";
import "./revenue.scss";

type RevenuePoint = { month: number; plan: number; fact: number; forecast: number; };
type RevenueMonth = { month: string; plan: number; fact: number; forecast: number; };
type LoadedRevenue = { year: number; months: RevenueMonth[] };

const MONTH_NAMES = [
    "Январь","Февраль","Март","Апрель","Май","Июнь",
    "Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"
];

const variants = {
    enter: (direction: number) => ({ x: direction > 0 ? 140 : -140, opacity: 0, position: "absolute" as const, width: "100%", top: 0 }),
    center: { x: 0, opacity: 1, position: "relative" as const, width: "100%", top: 0 },
    exit: (direction: number) => ({ x: direction < 0 ? 140 : -140, opacity: 0, position: "absolute" as const, width: "100%", top: 0 }),
};

type Props = { year?: number; idApi?: string | null; };

function unwrap<T>(out: any): T {
    if (out && Array.isArray(out)) return out as T;
    if (out && typeof out === "object") {
        if (Array.isArray(out.data)) return out.data as T;
        if (out.data !== undefined) return out.data as T;
    }
    return out as T;
}

const qs = (obj: Record<string, string | number | undefined | null>) =>
    new URLSearchParams(
        Object.entries(obj).reduce<Record<string,string>>((a,[k,v])=>{
            if (v !== undefined && v !== null && v !== "") a[k]=String(v);
            return a;
        },{})
    ).toString();

function metric(row: any, keys: string[]): number | null {
    if (!row || typeof row !== "object") return null;

    const key = keys.find((candidate) => row[candidate] !== undefined && row[candidate] !== null);
    if (!key) return null;

    const raw = row[key];
    if (typeof raw === "boolean" || (typeof raw === "string" && raw.trim() === "")) return null;

    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 ? value : null;
}

function normalizePoint(row: any, expectedMonth?: number): RevenuePoint | null {
    if (!row || typeof row !== "object") return null;

    const responseMonth = row.month === undefined || row.month === null
        ? null
        : Number(row.month);
    if (expectedMonth !== undefined && responseMonth !== null && responseMonth !== expectedMonth) {
        return null;
    }

    const month = expectedMonth ?? responseMonth;
    const plan = metric(row, ["plan", "plan_sum", "planAmount"]);
    const fact = metric(row, ["fact", "fact_sum", "factAmount"]);
    const forecast = metric(row, ["forecast", "forecast_sum", "forecastAmount"]);

    if (month === null || !Number.isInteger(month) || month < 1 || month > 12 || plan === null || fact === null || forecast === null) {
        return null;
    }

    return { month, plan, fact, forecast };
}

function normalizeCompleteYear(points: unknown): RevenuePoint[] | null {
    if (!Array.isArray(points) || points.length !== 12) return null;

    const byMonth = new Map<number, RevenuePoint>();
    for (const rawPoint of points) {
        const point = normalizePoint(rawPoint);
        if (!point || byMonth.has(point.month)) return null;
        byMonth.set(point.month, point);
    }

    if (byMonth.size !== 12) return null;
    return Array.from({ length: 12 }, (_, index) => byMonth.get(index + 1) as RevenuePoint);
}

export const Revenue: React.FC<Props> = ({ year, idApi = null }) => {
    const { value: activeCompanyId, resolved: activeCompanyResolved } = useActiveCompanyId();
    const { value: activeCompanyIdApi, resolved: activeCompanyApiResolved } = useActiveCompanyIdApi();
    const [period, setPeriod] = useState(() => {
        const today = new Date();
        return { year: year ?? today.getFullYear(), month: today.getMonth() };
    });
    const active = period.month;
    const Y = period.year;
    const [direction, setDirection] = useState(0);
    const [data, setData] = useState<LoadedRevenue | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<{ year: number; message: string } | null>(null);
    const [retryRequest, setRetryRequest] = useState(0);

    const abortRef = useRef<AbortController | null>(null);
    const requestRef = useRef(0);
    const explicitIdApi = idApi !== null && idApi !== "";
    const selectedIdApi = explicitIdApi ? idApi : activeCompanyIdApi;
    const companySelectionResolved = explicitIdApi
        ? true
        : activeCompanyResolved && activeCompanyApiResolved;
    const companySelectionAvailable = explicitIdApi
        ? true
        : Boolean(activeCompanyId);

    useEffect(() => {
        if (year !== undefined) setPeriod((current) => current.year === year ? current : { ...current, year });
    }, [year]);

    const paginate = useCallback((newIndex: number) => {
        if (newIndex < 0 || newIndex > 11 || newIndex === active) return;
        setDirection(newIndex > active ? 1 : -1);
        setPeriod({ year: Y, month: newIndex });
    }, [active, Y]);

    const stepMonth = useCallback((step: -1 | 1) => {
        const next = new Date(Y, active + step, 1);
        const nextYear = next.getFullYear();
        if (nextYear < 2000 || nextYear > 3000) return;
        setDirection(step);
        setPeriod({ year: nextYear, month: next.getMonth() });
    }, [active, Y]);

    const swipeHandlers = useSwipeable({
        onSwipedLeft: (gesture) => { if (isIntentionalMonthSwipe(gesture)) stepMonth(1); },
        onSwipedRight: (gesture) => { if (isIntentionalMonthSwipe(gesture)) stepMonth(-1); },
        trackMouse: true,
        delta: 45,
    });

    useEffect(() => {
        abortRef.current?.abort();
        const requestId = ++requestRef.current;

        if (!companySelectionResolved) {
            setData(null);
            setLoading(true);
            setError(null);
            return;
        }

        if (!companySelectionAvailable) {
            setData(null);
            setLoading(false);
            setError(null);
            return;
        }

        const ac = new AbortController();
        abortRef.current = ac;

        setData(null);
        setLoading(true);
        setError(null);

        const isCurrentRequest = () => !ac.signal.aborted && requestRef.current === requestId;

        const fetchYear = async (): Promise<RevenuePoint[] | null> => {
            try {
                const url = `/api/revenue/monthly?${qs({ year: Y, idApi: selectedIdApi ?? undefined })}`;
                const raw = await api.get(url, undefined, { signal: ac.signal });
                return normalizeCompleteYear(unwrap<unknown>(raw));
            } catch {
                return null;
            }
        };

        const fetchMonth = async (m: number): Promise<RevenuePoint | null> => {
            try {
                const url = `/api/plans?${qs({ month: m, year: Y, idApi: selectedIdApi ?? undefined })}`;
                const raw = await api.get(url, undefined, { signal: ac.signal });
                const arr = unwrap<any[]>(raw);

                if (!Array.isArray(arr)) return null;
                if (arr.length === 0) {
                    return { month: m, plan: 0, fact: 0, forecast: 0 };
                }

                const sums = { plan: 0, fact: 0, forecast: 0 };
                for (const row of arr) {
                    const point = normalizePoint(row, m);
                    if (!point) return null;
                    sums.plan += point.plan;
                    sums.fact += point.fact;
                    sums.forecast += point.forecast;
                }

                if (!Number.isFinite(sums.plan) || !Number.isFinite(sums.fact) || !Number.isFinite(sums.forecast)) {
                    return null;
                }

                return { month: m, plan: sums.plan, fact: sums.fact, forecast: sums.forecast };
            } catch {
                return null;
            }
        };

        const apply = (points: RevenuePoint[]) => {
            if (!isCurrentRequest()) return;

            const result: RevenueMonth[] = points.map((point, index) => ({
                month: MONTH_NAMES[index],
                plan: point.plan,
                fact: point.fact,
                forecast: point.forecast,
            }));

            setData({ year: Y, months: result });
            setLoading(false);
        };

        const run = async () => {
            const ydata = await fetchYear();
            if (!isCurrentRequest()) return;
            if (ydata) { apply(ydata); return; }

            const promises: Promise<RevenuePoint | null>[] = [];
            for (let m = 1; m <= 12; m++) promises.push(fetchMonth(m));
            const arr = await Promise.all(promises);
            if (!isCurrentRequest()) return;
            if (arr.some((point) => point === null)) {
                throw new Error("Не удалось загрузить данные за все 12 месяцев");
            }

            const completeFallback = normalizeCompleteYear(arr);
            if (!completeFallback) {
                throw new Error("Сервер вернул неполные данные по выручке");
            }

            apply(completeFallback);
        };

        run().catch((e: any) => {
            if (!isCurrentRequest()) return;
            setError({ year: Y, message: e?.message || "Ошибка загрузки" });
            setLoading(false);
        });

        return () => ac.abort();
    }, [
        Y,
        activeCompanyApiResolved,
        activeCompanyId,
        activeCompanyIdApi,
        activeCompanyResolved,
        companySelectionAvailable,
        companySelectionResolved,
        selectedIdApi,
        retryRequest,
    ]);

    const dataForYear = data?.year === Y ? data.months : null;
    const visibleError = error?.year === Y ? error.message : null;
    const displayLoading = loading || (!visibleError && companySelectionAvailable && !dataForYear);
    const months: RevenueMonth[] = useMemo(() => {
        if (dataForYear) return dataForYear;
        return MONTH_NAMES.map((m) => ({ month: m, plan: 0, fact: 0, forecast: 0 }));
    }, [dataForYear]);

    const idx = Math.min(active, months.length - 1);
    const { month, plan, fact, forecast } = months[idx];

    const R = 40;
    const C = 50;
    const length = 2 * Math.PI * R;

    const displayForecast = Math.max(0, forecast);
    const chartBase = Math.max(0, displayForecast, plan, fact);
    const safePlan = Math.max(0, Math.min(plan, chartBase));
    const safeFactAbs = Math.max(0, Math.min(fact, chartBase));

    const planPercent = chartBase > 0 ? (safePlan / chartBase) * 100 : 0;
    const factExtra = Math.max(0, safeFactAbs - safePlan);
    const factPercent = chartBase > 0 ? (factExtra / chartBase) * 100 : 0;
    const forecastPercent = Math.max(0, 100 - planPercent - factPercent);

    const planLen = (planPercent / 100) * length;
    const factLen = (factPercent / 100) * length;
    const forecastLen = (forecastPercent / 100) * length;

    const planOffset = 0;
    const factOffset = -planLen;
    const forecastOffset = -planLen - factLen;

    const mainPercent = plan > 0 ? Math.round((fact / plan) * 100) : 0;
    const barFactPercent = Math.round((safeFactAbs / (chartBase || 1)) * 100);

    return (
        <>
            <h2 className="section-title" id="revenue">
                Выручка {Y}
            </h2>

            <div
                className="card revenue-card"
                style={{ overflow: "hidden", minHeight: 210 }}
                onKeyDown={(e) => {
                    if (e.key === "ArrowLeft" && !displayLoading) stepMonth(-1);
                    if (e.key === "ArrowRight" && !displayLoading) stepMonth(1);
                }}
                tabIndex={0}
                aria-label="Диаграмма выручки по месяцам"
            >
                {displayLoading && <div className="revenue-loading">Загрузка…</div>}
                {!displayLoading && !companySelectionAvailable && (
                    <div className="revenue-loading">
                        Нет доступных точек.
                    </div>
                )}
                {visibleError && !displayLoading && <div className="revenue-error" role="alert">
                    Ошибка: {visibleError}
                    <button type="button" onClick={() => setRetryRequest((value) => value + 1)}>Повторить</button>
                </div>}

                {!displayLoading && companySelectionAvailable && !visibleError && (
                    <>
                    <div className="revenue-swipe-area" {...swipeHandlers}>
                    <div className="month-heading">
                        <button type="button" className="month-step" aria-label="Предыдущий месяц выручки"
                                disabled={Y === 2000 && active === 0} onClick={() => stepMonth(-1)}>‹</button>
                        <div className="revenue-month">{month}</div>
                        <button type="button" className="month-step" aria-label="Следующий месяц выручки"
                                disabled={Y === 3000 && active === 11} onClick={() => stepMonth(1)}>›</button>
                    </div>
                    <AnimatePresence custom={direction} mode="popLayout">
                        <motion.div
                            key={`${Y}-${active}`}
                            custom={direction}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ type: "spring", stiffness: 340, damping: 35, mass: 0.9 }}
                            style={{ width: "100%" }}
                        >
                            <div className="revenue-content">
                                <div className="revenue-left">
                                    {[
                                        { label: "План", value: safePlan.toLocaleString(), cls: "plan", percent: Math.round(planPercent) },
                                        { label: "Факт", value: safeFactAbs.toLocaleString(), cls: "fact", percent: barFactPercent },
                                        { label: "Прогноз\u00A0", value: displayForecast.toLocaleString(), cls: "forecast", percent: 100 },
                                    ].map((item) => (
                                        <div key={item.label} className="progress-row">
                                            <span>{item.label}</span>
                                            <span>{item.value}</span>
                                            <div className="progress-bar">
                                                <div className={`progress-fill ${item.cls}`} style={{ width: `${item.percent}%` }}></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="revenue-right">
                                    <div className="circle-chart">
                                        <svg viewBox="0 0 100 100" role="img" aria-label={`Выполнение плана: ${mainPercent}%`}>
                                            <circle className="bg" cx={C} cy={C} r={R} stroke="#f2f2f2" strokeWidth={10} fill="none" />
                                            <circle className="plan" cx={C} cy={C} r={R} stroke="#2c204b" strokeWidth={10} fill="none"
                                                    strokeDasharray={`${planLen} ${length - planLen}`} strokeDashoffset={planOffset} opacity={0.23}
                                                    style={{ transition: "stroke-dasharray 0.45s, stroke-dashoffset 0.45s" }} />
                                            <circle className="fact" cx={C} cy={C} r={R} stroke="url(#grad1)" strokeWidth={10} fill="none"
                                                    strokeDasharray={`${factLen} ${length - factLen}`} strokeDashoffset={factOffset} opacity={1}
                                                    style={{ transition: "stroke-dasharray 0.45s, stroke-dashoffset 0.45s" }} />
                                            <circle className="forecast" cx={C} cy={C} r={R} stroke="url(#grad2)" strokeWidth={10} fill="none"
                                                    strokeDasharray={`${forecastLen} ${length - forecastLen}`} strokeDashoffset={forecastOffset} opacity={0.8}
                                                    style={{ transition: "stroke-dasharray 0.45s, stroke-dashoffset 0.45s" }} />
                                            <defs>
                                                <linearGradient id="grad1" x1="0%" y1="0%" x2="100%" y2="0%">
                                                    <stop offset="0%" stopColor="#e72f44" />
                                                    <stop offset="100%" stopColor="#762e98" />
                                                </linearGradient>
                                                <linearGradient id="grad2" x1="0%" y1="0%" x2="100%" y2="0%">
                                                    <stop offset="0%" stopColor="#f05a63" />
                                                    <stop offset="100%" stopColor="#ffe066" />
                                                </linearGradient>
                                            </defs>
                                        </svg>
                                        <div className="circle-text"><span>{mainPercent}%</span></div>
                                    </div>
                                </div>
                            </div>

                        </motion.div>
                    </AnimatePresence>
                    </div>
                    <div className="month-dots" aria-label="Навигация по месяцам выручки">
                        {MONTH_NAMES.map((_, i) => (
                            <button type="button" key={i} onClick={() => paginate(i)}
                                    aria-current={i === active ? "date" : undefined}
                                    aria-label={`Перейти к месяцу ${MONTH_NAMES[i]} ${Y}`} />
                        ))}
                    </div>
                    </>
                )}
            </div>
        </>
    );
};
